import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Send,
  Lightbulb,
  Check,
  X,
  Heart,
  Shield,
} from "lucide-react";
import { DID_YOU_KNOW, MISSION_CARDS } from "../lib/constants";
import { listStories, submitStory } from "../lib/api";
import { toast } from "sonner";

const MOODS = [
  { value: "brave", label: "Brave" },
  { value: "curious", label: "Curious" },
  { value: "scared", label: "Scared" },
  { value: "proud", label: "Proud" },
  { value: "hopeful", label: "Hopeful" },
];

export default function KidsPage() {
  const [factIdx, setFactIdx] = useState(0);
  const [missionIdx, setMissionIdx] = useState(0);
  const [missionPicked, setMissionPicked] = useState(null);
  const [missionScore, setMissionScore] = useState(0);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [story, setStory] = useState("");
  const [mood, setMood] = useState("brave");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [confetti, setConfetti] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setFactIdx((i) => (i + 1) % DID_YOU_KNOW.length);
    }, 6500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    listStories(8).then(setRecent).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !story.trim() || !age) {
      toast.error("Add your name, age, and a short story so we can hear you.");
      return;
    }
    const ageNum = parseInt(age, 10);
    if (Number.isNaN(ageNum) || ageNum < 4 || ageNum > 21) {
      toast.error("Pick an age between 4 and 21.");
      return;
    }
    setSubmitting(true);
    try {
      const saved = await submitStory({
        name: name.trim(),
        age: ageNum,
        story: story.trim(),
        mood,
      });
      setRecent((r) => [saved, ...r].slice(0, 8));
      setName("");
      setAge("");
      setStory("");
      setMood("brave");
      setConfetti(true);
      toast.success("Your story is in. You just made the network stronger.");
      setTimeout(() => setConfetti(false), 1800);
    } catch (err) {
      toast.error("Couldn't send right now. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickMission = (answer) => {
    if (missionPicked !== null) return;
    const correct = MISSION_CARDS[missionIdx].answer === answer;
    setMissionPicked(answer);
    if (correct) setMissionScore((s) => s + 1);
  };

  const nextMission = () => {
    setMissionPicked(null);
    setMissionIdx((i) => (i + 1) % MISSION_CARDS.length);
  };

  const fact = DID_YOU_KNOW[factIdx];
  const mission = MISSION_CARDS[missionIdx];
  const missionCorrect = missionPicked !== null && missionPicked === mission.answer;

  return (
    <div data-testid="kids-page" className="min-h-screen bg-[#FAFAFA] bg-grain pt-20 sm:pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm text-[#1A1414]/60 hover:text-[#E60000] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back to the worlds
        </Link>

        <div className="mb-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#F5A623] mb-2">
            Pillar 03 / Kids Interactive
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A1414] tracking-tighter text-balance">
            Your voice is the <span className="text-[#E60000]">superpower.</span>
          </h1>
          <p className="mt-3 text-base text-[#1A1414]/70">
            Tell us your story, take quick true/false missions, and unlock Did‑You‑Know
            facts about bullying, online scams and digital safety.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Did you know */}
          <section className="lg:col-span-7 relative p-6 sm:p-8 rounded-3xl bg-[#1A1414] text-white overflow-hidden" data-testid="did-you-know">
            <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={20} className="text-[#FF8080]" />
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF8080]">
                  Did You Know?
                </span>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={factIdx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  data-testid="fact-card"
                >
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50 mb-1">
                    {fact.category}
                  </p>
                  <p className="font-display font-black text-2xl sm:text-3xl leading-tight tracking-tight">
                    {fact.fact}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="flex gap-2 mt-6">
                {DID_YOU_KNOW.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setFactIdx(i)}
                    data-testid={`fact-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === factIdx ? "w-8 bg-[#FF8080]" : "w-3 bg-white/20 hover:bg-white/40"
                    }`}
                    aria-label={`Fact ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Mission cards (true/false) */}
          <section
            className="lg:col-span-5 p-6 sm:p-8 rounded-3xl bg-white border-2 border-[#FF8080]/30 shadow-arc"
            data-testid="mission-card"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000]">
                Quick Mission
              </span>
              <span className="text-xs font-bold text-[#1A1414]/70">
                Score: <span className="text-[#E60000]">{missionScore}</span>
              </span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={missionIdx}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="font-display font-black text-xl text-[#1A1414] leading-snug">
                  {mission.statement}
                </h3>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button
                    onClick={() => pickMission(true)}
                    disabled={missionPicked !== null}
                    data-testid="mission-true-btn"
                    className={`p-4 rounded-2xl border-2 font-display font-bold transition-all flex items-center justify-center gap-2 ${
                      missionPicked === true
                        ? missionCorrect
                          ? "bg-emerald-50 border-emerald-500 text-emerald-900"
                          : "bg-[#FF8080]/15 border-[#E60000] text-[#8B0000]"
                        : "bg-white border-[#FF8080]/30 hover:border-[#E60000] hover:-translate-y-0.5"
                    }`}
                  >
                    <Check size={18} /> True
                  </button>
                  <button
                    onClick={() => pickMission(false)}
                    disabled={missionPicked !== null}
                    data-testid="mission-false-btn"
                    className={`p-4 rounded-2xl border-2 font-display font-bold transition-all flex items-center justify-center gap-2 ${
                      missionPicked === false
                        ? missionCorrect
                          ? "bg-emerald-50 border-emerald-500 text-emerald-900"
                          : "bg-[#FF8080]/15 border-[#E60000] text-[#8B0000]"
                        : "bg-white border-[#FF8080]/30 hover:border-[#E60000] hover:-translate-y-0.5"
                    }`}
                  >
                    <X size={18} /> False
                  </button>
                </div>

                {missionPicked !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 rounded-2xl bg-[#FF8080]/10 border border-[#FF8080]/30 text-sm text-[#1A1414]"
                    data-testid="mission-insight"
                  >
                    <strong className="text-[#8B0000]">Why:</strong> {mission.insight}
                  </motion.div>
                )}

                {missionPicked !== null && (
                  <button
                    onClick={nextMission}
                    data-testid="mission-next-btn"
                    className="mt-4 w-full px-5 py-3 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] transition-all"
                  >
                    Next mission
                  </button>
                )}
              </motion.div>
            </AnimatePresence>
          </section>

          {/* Share your story */}
          <section
            className="lg:col-span-7 relative p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-white to-[#FF8080]/20 border-2 border-[#FF8080]/30 shadow-arc overflow-hidden"
            data-testid="story-form-section"
          >
            <AnimatePresence>
              {confetti && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="pointer-events-none absolute inset-0 z-10"
                >
                  {Array.from({ length: 24 }).map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{
                        x: "50%",
                        y: "50%",
                        opacity: 1,
                        rotate: 0,
                      }}
                      animate={{
                        x: `${Math.random() * 100}%`,
                        y: `${Math.random() * 100}%`,
                        opacity: 0,
                        rotate: Math.random() * 540,
                      }}
                      transition={{ duration: 1.6, ease: "easeOut" }}
                      className="absolute w-2 h-3 rounded-sm"
                      style={{
                        background: ["#E60000", "#FF8080", "#F5A623", "#8B0000"][i % 4],
                      }}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={20} className="text-[#E60000]" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000]">
                Share Your Story
              </span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#1A1414]">
              What&apos;s something you handled — or wish you had?
            </h2>
            <p className="text-sm text-[#1A1414]/70 mt-2 max-w-xl">
              No real names of other people. No locations. Just your truth. Your story
              helps other kids feel less alone.
            </p>

            <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="story-form">
              <input
                type="text"
                placeholder="Your first name or nickname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                data-testid="story-name-input"
                className="px-4 py-3 rounded-xl bg-white border-2 border-[#FF8080]/30 focus:border-[#E60000] focus:outline-none focus:ring-2 focus:ring-[#E60000]/20 text-[#1A1414] placeholder:text-[#1A1414]/40 transition-all"
              />
              <input
                type="number"
                placeholder="Your age (4–21)"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                min={4}
                max={21}
                required
                data-testid="story-age-input"
                className="px-4 py-3 rounded-xl bg-white border-2 border-[#FF8080]/30 focus:border-[#E60000] focus:outline-none focus:ring-2 focus:ring-[#E60000]/20 text-[#1A1414] placeholder:text-[#1A1414]/40 transition-all"
              />
              <textarea
                placeholder="Your story — what happened, what you did, what you wish you knew…"
                value={story}
                onChange={(e) => setStory(e.target.value)}
                required
                minLength={8}
                maxLength={2000}
                rows={5}
                data-testid="story-text-input"
                className="sm:col-span-2 px-4 py-3 rounded-xl bg-white border-2 border-[#FF8080]/30 focus:border-[#E60000] focus:outline-none focus:ring-2 focus:ring-[#E60000]/20 text-[#1A1414] placeholder:text-[#1A1414]/40 transition-all resize-none"
              />
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A1414]/60">
                  Mood:
                </span>
                {MOODS.map((m) => (
                  <button
                    type="button"
                    key={m.value}
                    onClick={() => setMood(m.value)}
                    data-testid={`mood-${m.value}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      mood === m.value
                        ? "bg-[#E60000] text-white shadow-arc"
                        : "bg-white border border-[#FF8080]/40 text-[#1A1414] hover:border-[#E60000]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="story-submit-btn"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] hover:-translate-y-0.5 transition-all disabled:opacity-60"
              >
                <Send size={16} />
                {submitting ? "Sending…" : "Send my story"}
              </button>
            </form>
          </section>

          {/* Recent stories */}
          <section
            className="lg:col-span-5 p-6 sm:p-8 rounded-3xl bg-white border-2 border-[#FF8080]/30 shadow-arc"
            data-testid="recent-stories"
          >
            <div className="flex items-center gap-2 mb-3">
              <Heart size={20} className="text-[#E60000] fill-[#E60000]" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000]">
                Recent Voices
              </span>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-[#1A1414]/60">
                No stories yet. Be the first to break the silence.
              </p>
            ) : (
              <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {recent.map((s) => (
                  <li
                    key={s.id}
                    className="p-3 rounded-xl bg-[#FF8080]/10 border border-[#FF8080]/20"
                    data-testid={`story-item-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-display font-bold text-sm text-[#8B0000]">
                        {s.name}{" "}
                        <span className="text-[10px] font-normal text-[#1A1414]/50">
                          · age {s.age}
                        </span>
                      </span>
                      <span className="text-[10px] uppercase tracking-wide font-bold text-[#E60000]">
                        {s.mood}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A1414]/80 leading-snug line-clamp-3">
                      {s.story}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Pledge */}
          <section className="lg:col-span-12 p-6 sm:p-8 rounded-3xl bg-[#E60000] text-white relative overflow-hidden" data-testid="pledge-card">
            <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
            <div className="relative grid md:grid-cols-[auto_1fr] gap-4 items-center">
              <Shield size={56} className="text-white/95 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-white/80 mb-1">
                  The Mission X Pledge
                </p>
                <h3 className="font-display font-black text-2xl sm:text-3xl leading-tight">
                  I will speak up. I will protect my friends. I will see what
                  isn&apos;t being said.
                </h3>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
